import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "react-bootstrap";
import { WarningCallout, BackLink, Card, Button, Input, ErrorSummary } from "nhsuk-react-components";
import { lookupEmergencyAccess } from "../../actions/patientActions";
import { storeEmergencyToken } from "../../utils/emergencyAccessStorage";

export default function EmergencyPatientAccessScreen() {
    const dispatch = useDispatch();
    const history = useHistory();
    const [patientNumber, setPatientNumber] = useState('');
    const [surname, setSurname] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = "Emergency Patient Access";
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        const cleanedNumber = patientNumber.replace(/\s/g, '');
        if (!cleanedNumber || !surname.trim() || !dateOfBirth) {
            setError('Please enter NHS or identification number, surname and date of birth.');
            return;
        }
        setSubmitting(true);
        try {
            const result = await dispatch(lookupEmergencyAccess(
                cleanedNumber,
                surname.trim(),
                new Date(dateOfBirth).toISOString(),
                email.trim() || null
            ));
            storeEmergencyToken(result.patientId, result.token, result.expiresAt);
            history.push(`/patient/emergency/${result.patientId}`);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                'No matching patient found. Please check the details and try again.'
            );
            setSubmitting(false);
        }
    };

    return (
        <div className="nhsuk-width-container">
            <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                <Container fluid>
                    <BackLink href="/">Go back</BackLink>
                    <h1>Emergency Patient Access</h1>

                    <WarningCallout>
                        <WarningCallout.Label>For clinical emergencies only</WarningCallout.Label>
                        <p>Enter the patient's NHS number or identification number, surname and date of birth to access their cardiac record.</p>
                    </WarningCallout>

                    <Card>
                        <Card.Content>
                            {error && (
                                <ErrorSummary aria-labelledby="lookup-error-title" role="alert">
                                    <ErrorSummary.Title id="lookup-error-title">Access denied</ErrorSummary.Title>
                                    <ErrorSummary.Body><p>{error}</p></ErrorSummary.Body>
                                </ErrorSummary>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="nhsuk-form-group">
                                    <label className="nhsuk-label" htmlFor="patientNumber">
                                        <strong>NHS number or identification number</strong>
                                    </label>
                                    <Input
                                        id="patientNumber"
                                        name="patientNumber"
                                        type="text"
                                        value={patientNumber}
                                        onChange={(e) => setPatientNumber(e.target.value)}
                                        disabled={submitting}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="nhsuk-form-group">
                                    <label className="nhsuk-label" htmlFor="surname">
                                        <strong>Patient surname</strong>
                                    </label>
                                    <Input
                                        id="surname"
                                        name="surname"
                                        type="text"
                                        value={surname}
                                        onChange={(e) => setSurname(e.target.value)}
                                        disabled={submitting}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="nhsuk-form-group">
                                    <label className="nhsuk-label" htmlFor="email">
                                        <strong>Your email address</strong>
                                    </label>
                                    <span className="nhsuk-hint">Optional — for audit purposes only</span>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={submitting}
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="nhsuk-form-group">
                                    <label className="nhsuk-label" htmlFor="dob">
                                        <strong>Patient date of birth</strong>
                                    </label>
                                    <input
                                        id="dob"
                                        className="nhsuk-input nhsuk-input--width-10"
                                        name="dob"
                                        type="date"
                                        value={dateOfBirth}
                                        onChange={(e) => setDateOfBirth(e.target.value)}
                                        disabled={submitting}
                                    />
                                </div>

                                <Button type="submit" disabled={submitting}>
                                    {submitting ? 'Verifying…' : 'Access patient record'}
                                </Button>
                            </form>
                        </Card.Content>
                    </Card>
                </Container>
            </main>
        </div>
    );
}
